import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { MapPin, Search } from 'lucide-react';
import { useMemo, useState } from 'react';

interface ScheduleSlot {
  date: string;
  startTime: string;
  endTime: string;
  providerId: string;
  providerName?: string | null;
  facilityId?: string | null;
  facilityName?: string | null;
  status: string;
  patientName: string | null;
  appointmentType?: string | null;
  fullAppointmentTypes?: string[] | null;
  notes: string | null;
  appointmentId?: string | null;
  patientId?: string | null;
}

interface ScheduleData {
  providers: Array<{ id: string; name: string }>;
  slots: ScheduleSlot[];
}

interface ScheduleDisplayProps {
  scheduleData: ScheduleData;
  selectedDate: string;
  selectedFacilityName?: string;
}

export function ScheduleDisplay({
  scheduleData,
  selectedDate,
  selectedFacilityName,
}: ScheduleDisplayProps) {
  const [patientSearchTerm, setPatientSearchTerm] = useState('');

  const filteredSlots = useMemo(() => {
    if (!patientSearchTerm) return scheduleData.slots;
    const lowerSearch = patientSearchTerm.toLowerCase();
    return scheduleData.slots.filter(
      (slot) =>
        slot.patientName?.toLowerCase().includes(lowerSearch) ||
        slot.appointmentId?.toLowerCase().includes(lowerSearch) ||
        slot.notes?.toLowerCase().includes(lowerSearch)
    );
  }, [scheduleData.slots, patientSearchTerm]);

  const groupSlotsByTime = (slots: ScheduleSlot[]) => {
    const grouped: Record<string, ScheduleSlot[]> = {};
    const sortedSlots = [...slots].sort((a, b) => a.startTime.localeCompare(b.startTime));

    for (const slot of sortedSlots) {
      const timeKey = `${slot.startTime} - ${slot.endTime}`;
      if (!grouped[timeKey]) {
        grouped[timeKey] = [];
      }
      grouped[timeKey].push(slot);
    }

    return grouped;
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'booked':
        return 'bg-blue-100 border-blue-300';
      case 'available':
        return 'bg-green-100 border-green-300';
      case 'unavailable':
        return 'bg-gray-100 border-gray-300';
      default:
        return 'bg-white border-gray-200';
    }
  };

  const calculateStats = (slots: ScheduleSlot[]) => {
    const stats = {
      'Total Slots': slots.length,
      Booked: 0,
      Available: 0,
      Unavailable: 0,
    };

    for (const slot of slots) {
      const status = slot.status;
      if (status === 'Booked') stats.Booked++;
      else if (status === 'Available') stats.Available++;
      else if (status === 'Unavailable') stats.Unavailable++;
    }

    return stats;
  };

  if (scheduleData.slots.length === 0) {
    return (
      <Card>
        <CardContent className="text-center py-8">
          <p className="text-muted-foreground">
            No schedule data found for the selected date and provider.
          </p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      {/* Overview */}
      <Card>
        <CardHeader>
          <CardTitle>Schedule Overview</CardTitle>
          <CardDescription>
            {scheduleData.providers[0]?.name || 'Provider'}
            {selectedFacilityName && ` • ${selectedFacilityName}`} • {selectedDate} •{' '}
            {scheduleData.slots.length} slots
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">
            All times are displayed in America/New_York timezone
          </p>
        </CardContent>
      </Card>

      {/* Patient Search */}
      <Card>
        <CardHeader>
          <CardTitle>Search</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by patient name, appointment ID, or notes..."
              value={patientSearchTerm}
              onChange={(e) => setPatientSearchTerm(e.target.value)}
              className="pl-8"
            />
          </div>
          {patientSearchTerm && (
            <p className="text-sm text-muted-foreground mt-2">
              Showing {filteredSlots.length} of {scheduleData.slots.length} slots
            </p>
          )}
        </CardContent>
      </Card>

      {/* Schedule Grid */}
      <Card>
        <CardHeader>
          <CardTitle>Schedule Details</CardTitle>
        </CardHeader>
        <CardContent>
          {filteredSlots.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No results found for &quot;{patientSearchTerm}&quot;
            </div>
          ) : (
            <div className="space-y-2">
              {Object.entries(groupSlotsByTime(filteredSlots)).map(([timeKey, slots]) => (
                <div
                  key={timeKey}
                  className={`border rounded-lg p-4 ${slots.length > 1 ? 'border-orange-400 bg-orange-50' : 'border-gray-200'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-semibold text-sm">{timeKey}</span>
                    {slots.length > 1 && (
                      <span className="bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold">
                        Multiple Bookings ({slots.length})
                      </span>
                    )}
                  </div>
                  <div className="space-y-2">
                    {slots.map((slot, idx) => (
                      <div
                        key={`${timeKey}-${idx}-${slot.appointmentId || slot.startTime}`}
                        className={`p-3 rounded border ${getStatusColor(slot.status)}`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-start gap-2 flex-wrap">
                              <div className="font-medium">
                                {slot.patientName || slot.appointmentType || slot.status}
                              </div>
                              {/* Facility badge - clean and professional */}
                              <Badge variant="outline" className="text-xs gap-1">
                                <MapPin className="h-3 w-3" />
                                {slot.facilityName || `Facility ${slot.facilityId}`}
                              </Badge>
                            </div>
                            {slot.appointmentType && (
                              <div className="text-sm text-gray-600 mt-1">
                                Type: {slot.appointmentType}
                              </div>
                            )}
                            {slot.fullAppointmentTypes && slot.fullAppointmentTypes.length > 0 && (
                              <div className="text-sm text-gray-600">
                                All Types: {slot.fullAppointmentTypes.join(', ')}
                              </div>
                            )}
                            {slot.patientName && (
                              <div className="text-sm text-gray-600">
                                Patient: {slot.patientName}
                              </div>
                            )}
                            {slot.appointmentId && (
                              <div className="text-xs text-gray-500 mt-1">
                                ID: {slot.appointmentId}
                              </div>
                            )}
                            {slot.notes && (
                              <div className="text-xs text-gray-500 mt-1">{slot.notes}</div>
                            )}
                          </div>
                          <span
                            className={`px-2 py-1 rounded text-xs font-medium ${
                              slot.status === 'Booked'
                                ? 'bg-blue-600 text-white'
                                : slot.status === 'Available'
                                  ? 'bg-green-600 text-white'
                                  : 'bg-gray-600 text-white'
                            }`}
                          >
                            {slot.status}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Statistics */}
      <Card>
        <CardHeader>
          <CardTitle>Schedule Statistics</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {Object.entries(calculateStats(filteredSlots)).map(([label, value]) => (
              <div key={label} className="text-center p-4 bg-gray-50 rounded-lg">
                <div className="text-2xl font-bold">{value}</div>
                <div className="text-sm text-muted-foreground">{label}</div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </>
  );
}
